import update from 'immutability-helper';
import { range, countBy } from 'lodash-unified';
import { isNullish } from '../typeValidation/nullish.js';

function arrayToObj(array, idKey = 'id') {
  if (typeof idKey === 'string') {
    return Object.fromEntries(array.map((arrayItem, index) => {
      let key = index.toString();

      if (typeof arrayItem === 'object') {
        key = arrayItem[idKey];
      }

      return [key, arrayItem];
    }));
  }

  return Object.fromEntries(array.map(arrayItem => [idKey(arrayItem), arrayItem]));
}
/**
 * Takes an array of ByID<> objects and flattens it to a single object.
 *
 * Ex: ByID<Booking>[] => ByID<Booking>
 */

const byIdArrayToObj = array => array.reduce((obj, currentValue) => Object.assign(Object.assign({}, obj), currentValue), {});
/**
 * Takes an array of string and returns an object with as {[string]: boolean}
 *
 * Ex: [id1, id2] => { id1: true, id2: true }
 */

const idArrayToObjBool = array => array.reduce((a, v) => Object.assign(Object.assign({}, a), {
  [v]: true
}), {});
/**
 * Flattens array. Supports one level of nesting. Example:
 * ```
 * [[1, 2], [3], [4, 5]] => [1, 2, 3, 4, 5]
 * ```
 */

const flattenArray = array => [].concat(...array);
/**
 * Removes exact duplicates (`===`) from array. Suitable for literal types.
 * ```
 * [1, 1, 2, 3] => [1, 2, 3]
 * ```
 */

const deduplicateArray = array => [...new Set(array)];
/**
 * Removes duplicates from an array of objects via a property
 * ```
 * [{ secretID: '1' }, { secretID: '1' }] => [{ secretID: '1' }]
 * ```
 *
 * we can't use deDuplicateArrayOfObjectsByID here because that expects an
 * IDObj[] not a ByID
 */

const deduplicateArrayByProperty = (array, key) => {
  const seen = new Set();
  return array.filter(item => {
    const keyValue = item[key];

    if (seen.has(keyValue)) {
      return false;
    }

    seen.add(keyValue);
    return true;
  });
};
/**
 * Removes duplicates from an array of objects via id property
 * ```
 * [{ id: '1' }, { id: '1' }] => [{ id: '1' }]
 * ```
 */

const deduplicateArrayOfObjectsByID = array => deduplicateArrayByProperty(array, 'id');
/**
 * Given an array, looks for the first element that matches a condition
 * (`predicate`). If a match is found, returns a new array with the matching
 * element updated based on the `newElementCallback`. If no match is found,
 * returns a new array with a new element added at the end based on the
 * `newElementCallback`. If undefined or null is passed as `origArray`, returns
 * a new array with one element based on the `newElementCallback`.
 *
 * As this function is able to create an entirely new element,
 * `newElementCallback` must be written such that it can return an array element
 * that completely satisifies the type requirements.
 */

const updateOrAddToArray = (origArray, predicate, newElementCallback) => {
  var _a, _b;

  const indexOfExistingElement = (_a = origArray === null || origArray === void 0 ? void 0 : origArray.findIndex(predicate)) !== null && _a !== void 0 ? _a : -1;
  const existingElement = origArray === null || origArray === void 0 ? void 0 : origArray[indexOfExistingElement];
  const indexToRemove = existingElement ? indexOfExistingElement : (_b = origArray === null || origArray === void 0 ? void 0 : origArray.length) !== null && _b !== void 0 ? _b : 0;
  const countToRemove = existingElement ? 1 : 0;
  return update(origArray !== null && origArray !== void 0 ? origArray : [], {
    $splice: [[indexToRemove, countToRemove, newElementCallback(existingElement)]]
  });
};
/**
 * Given an array, looks for the first element that matches a condition
 * (`predicate`). If a match is found, returns a new array with the matching
 * element updated based on the `updatedElementCallback`. Otherwise returns
 * undefined.
 */

const updateInArray = (origArray, predicate, updatedElementCallback) => {
  var _a;

  const indexOfExistingElement = (_a = origArray === null || origArray === void 0 ? void 0 : origArray.findIndex(predicate)) !== null && _a !== void 0 ? _a : -1;
  const existingElement = origArray === null || origArray === void 0 ? void 0 : origArray[indexOfExistingElement];
  if (!existingElement) return;
  return update(origArray !== null && origArray !== void 0 ? origArray : [], {
    $splice: [[indexOfExistingElement, 1, updatedElementCallback(existingElement)]]
  });
};
/**
 * Given an array of type T, returns an array of T arrays, grouping by matching elements.
 */

const groupByElement = (arr, element) => {
  const hash = Object.create(null);
  const keyElement = element;
  const result = [];
  arr.forEach(el => {
    if (!hash[el[keyElement]]) {
      hash[el[keyElement]] = [];
      result.push(hash[el[keyElement]]);
    }

    hash[el[keyElement]].push(el);
  });
  return result;
};
/**
 * Find the smallest value by a key in an array of objects, and return the
 * object.
 *
 * Example:
 * ```
 * const ages = [{ name: 'Cody', age: 12 }, { name: 'Jordan', age: 20 }]
 * getMinInArray(ages, 'age') // { name: 'Cody', age: 12 }
 * ```
 */

const getMinInArray = (array, key) => {
  if (!array.length) return null;
  return array.reduce((prev, curr) => prev[key] < curr[key] ? prev : curr);
};
/**
 * Find the largest value by a key in an array of objects, and return the
 * object.
 *
 * Example:
 * ```
 * const ages = [{ name: 'Cody', age: 12 }, { name: 'Jordan', age: 20 }]
 * getMaxInArray(ages, 'age') // { name: 'Jordan', age: 20 }
 * ```
 */

const getMaxInArray = (array, key) => {
  if (!array.length) return null;
  return array.reduce((prev, curr) => prev[key] > curr[key] ? prev : curr);
};
/**
 * get the range array of string between a start and an end
 *
 * Example:
 * ['1', '4'] => ['1', '2', '3', '4']
 * [1, 4] => ['1', '2', '3', '4']
 */

const getRangeArrayFromStartEnd = (start, end) => range(Number(start), Number(end) + 1).map(number => number.toString());
/**
 * From array of strings, return occurence of each string
 * [5, 5, 5, 2, 2, 2, 2, 2, 9, 4] => {2: 5, 4: 1, 5: 3, 9: 1}
 */

const countOccurenceOfStringsInArray = array => countBy(array);
/**
 * Take an array of `key, value` objects, and convert it to an object.
 *
 * Example:
 * ```
 * [
 *   { key: 'abc', value: 0 },
 *   { key: 'xyz', value: 10 },
 * ]
 * ⬇️
 * { 'abc': 0, 'xyz': 10 }
 * ```
 */

const keyValueArrayToObj = arr => arr.reduce((obj, {
  key,
  value
}) => Object.assign(Object.assign({}, obj), {
  [key]: value
}), {});
/**
 * Get a spec for the addition (to end of array), removal or update (overwrite)
 * of an array element suitable to pass to immutability-helper `update()`.
 * Intended for use with ListAddRemoveItem. Does not support setting nullish
 * values. Throws errors for nullish required params for a given operation.
 *
 * TODO: Once on TS 5, consider implementing conditional types instead of or in
 * addition to error throwing.
 *
 * Usage examples:
 * ```
 * const array = [1, 2, 3];
 *
 * const spec1 = getSpecForArrayUpdate('add', { value: 4 });
 * const newArray1 = update(array, spec1); // [1, 2, 3, 4]
 *
 * const spec2 = getSpecForArrayUpdate('remove', { index: 1 });
 * const newArray2 = update(array, spec2); // [1, 3]
 *
 * const spec3 = getSpecForArrayUpdate('update', { index: 1, value: 5 });
 * const newArray3 = update(array, spec3); // [1, 5, 3]
 * ```
 */

const getSpecForArrayUpdate = (operation, params = {}) => {
  let spec;

  switch (operation) {
    case 'add':
      if (isNullish(params.value)) throw Error('Must pass value for addition');
      spec = {
        $push: [params.value]
      };
      break;

    case 'remove':
      if (isNullish(params.index)) throw Error('Must pass index for removal');
      spec = {
        $splice: [[params.index, 1]]
      };
      break;

    case 'update':
      if (isNullish(params.value)) throw Error('Must pass value for update');
      if (isNullish(params.index)) throw Error('Must pass index for update');
      spec = {
        [params.index]: {
          $set: params.value
        }
      };
  }

  return spec;
};

export { arrayToObj, byIdArrayToObj, countOccurenceOfStringsInArray, deduplicateArray, deduplicateArrayByProperty, deduplicateArrayOfObjectsByID, flattenArray, getMaxInArray, getMinInArray, getRangeArrayFromStartEnd, getSpecForArrayUpdate, groupByElement, idArrayToObjBool, keyValueArrayToObj, updateInArray, updateOrAddToArray };
